import { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";

function tryJsonParse(str) {
  try {
    return { ok: true, value: JSON.parse(str) };
  } catch {
    return { ok: false };
  }
}

function parseFlexibleString(value) {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  const first = trimmed[0];
  if (first !== "{" && first !== "[" && first !== '"') return value;

  const direct = tryJsonParse(trimmed);
  if (direct.ok) return direct.value;

  if (first === "{" || first === "[") {
    const unescaped = tryJsonParse(trimmed.replace(/\\"/g, '"'));
    if (unescaped.ok) return unescaped.value;
  }

  return value;
}

function deepParse(value, depth = 0) {
  if (depth > 12) return value;
  if (typeof value === "string") {
    const parsed = parseFlexibleString(value);
    if (parsed === value) return value;
    return deepParse(parsed, depth + 1);
  }
  if (Array.isArray(value)) {
    return value.map((item) => deepParse(item, depth + 1));
  }
  if (value && typeof value === "object") {
    const out = {};
    for (const key of Object.keys(value)) {
      out[key] = deepParse(value[key], depth + 1);
    }
    return out;
  }
  return value;
}

function legacyStringCleanup(raw) {
  try {
    let cleaned = raw.replace(/^"+|"+$/g, "");
    cleaned = cleaned.replace(/\\*"{3,}/g, '"');
    cleaned = cleaned.replace(/\\{1,}/g, "");
    return JSON.parse(`[${cleaned}]`);
  } catch {
    return null;
  }
}

function parseRawContent(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map((item) => deepParse(item));
  if (typeof raw === "object") return [deepParse(raw)];
  if (typeof raw !== "string") return [raw];

  const directParsed = parseFlexibleString(raw);
  if (directParsed !== raw) {
    const deep = deepParse(directParsed);
    if (Array.isArray(deep)) return deep;
    return [deep];
  }

  const wrapped = tryJsonParse(`[${raw}]`);
  if (wrapped.ok && Array.isArray(wrapped.value)) {
    return wrapped.value.map((item) => deepParse(item));
  }

  const legacy = legacyStringCleanup(raw);
  if (Array.isArray(legacy)) return legacy.map((item) => deepParse(item));

  return [raw];
}

function getSuccessState(item) {
  if (!item || typeof item !== "object") return null;
  if (item.isSuccess === true) return "success";
  if (item.isSuccess === false) return "failure";
  const status = String(item.status || item.current_status || "").toLowerCase();
  if (status.includes("fulfilled") || status === "completed") return "success";
  if (
    status.includes("rejected") ||
    status.includes("failed") ||
    status.includes("error")
  ) {
    return "failure";
  }
  return null;
}

function getPrimaryMessage(item) {
  if (!item || typeof item !== "object") return null;
  if (typeof item.message === "string") return item.message;
  if (typeof item.message?.message === "string") return item.message.message;
  if (typeof item.listStatus?.message?.message === "string") {
    return item.listStatus.message.message;
  }
  if (typeof item.status_request?.response_message === "string") {
    return item.status_request.response_message;
  }
  return null;
}

function resolveDataPayload(item) {
  const raw =
    item?.listStatus?.message?.config?.data ??
    item?.message?.config?.data ??
    null;
  if (raw == null) return null;
  if (typeof raw === "string") {
    const parsed = parseFlexibleString(raw);
    return parsed === raw ? raw : parsed;
  }
  return raw;
}

function buildSummary(item) {
  if (!item || typeof item !== "object") return [];
  const rows = [];

  const pushIf = (label, value) => {
    if (value === undefined || value === null || value === "") return;
    const str =
      typeof value === "object" ? JSON.stringify(value) : String(value);
    if (!str.trim()) return;
    rows.push([label, str]);
  };

  pushIf("Status Code", item.statusCode ?? item.status_code);
  pushIf(
    "HTTP",
    item.listStatus?.message?.status ?? item.message?.status ?? (typeof item.status === "number" ? item.status : null)
  );
  pushIf("Message", getPrimaryMessage(item));
  pushIf("Validation", item.validationStatus);
  pushIf(
    "URL",
    item.listStatus?.message?.config?.url ?? item.message?.config?.url
  );
  const method =
    item.listStatus?.message?.config?.method ?? item.message?.config?.method;
  if (method) pushIf("Method", String(method).toUpperCase());

  const payload = resolveDataPayload(item);
  const customer =
    (payload && typeof payload === "object" && payload.messages?.[0]) ||
    item.listStatus?.message?.config?.data?.messages?.[0] ||
    item.message?.config?.data?.messages?.[0];
  if (customer && typeof customer === "object") {
    const fullName = [customer.customerFirstName, customer.customerLastName]
      .filter(Boolean)
      .join(" ");
    pushIf("Cliente", fullName);
    pushIf("Email", customer.customerEmail);
    pushIf("Motivation Code", customer.motivationCode);
    pushIf("Product Code", customer.productCode);
  }

  return rows;
}

function extractStack(item) {
  if (!item || typeof item !== "object") return null;
  const stack =
    item.listStatus?.message?.stack ?? item.message?.stack ?? item.stack;
  return typeof stack === "string" ? stack : null;
}

const preStyle = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  maxWidth: "100%",
  fontSize: "12px",
  margin: 0,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 6,
  padding: 12,
  color: "#0f172a",
};

function StatusPill({ state }) {
  if (!state) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold uppercase tracking-wide bg-slate-100 text-slate-700 border-slate-200">
        Info
      </span>
    );
  }
  const isSuccess = state === "success";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold uppercase tracking-wide ${
        isSuccess
          ? "bg-emerald-100 text-emerald-800 border-emerald-200"
          : "bg-rose-100 text-rose-800 border-rose-200"
      }`}
    >
      {isSuccess ? "Success" : "Failure"}
    </span>
  );
}

function SummaryTable({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="mb-3">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
        Resumo
      </div>
      <div className="rounded border border-slate-200 overflow-hidden">
        <table className="w-full text-sm" style={{ tableLayout: "fixed" }}>
          <tbody>
            {rows.map(([label, value]) => (
              <tr
                key={label}
                className="border-b border-slate-100 last:border-0"
              >
                <td className="px-3 py-2 bg-slate-50 text-slate-500 font-medium align-top" style={{ width: 160 }}>
                  {label}
                </td>
                <td
                  className="px-3 py-2 text-slate-800"
                  style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                >
                  {value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-3">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function ItemCard({ item, index }) {
  const [showRaw, setShowRaw] = useState(false);
  const [showStack, setShowStack] = useState(false);

  const state = getSuccessState(item);
  const primaryMessage = getPrimaryMessage(item);
  const summary = buildSummary(item);
  const payload = resolveDataPayload(item);
  const stack = extractStack(item);

  const headerMessage = useMemo(() => {
    if (typeof item === "string") return item.slice(0, 300);
    return (
      primaryMessage ||
      item?.validationStatus ||
      (state === "success"
        ? "Success"
        : state === "failure"
        ? "Error"
        : "Details")
    );
  }, [item, primaryMessage, state]);

  const borderColor =
    state === "success"
      ? "border-emerald-200"
      : state === "failure"
      ? "border-rose-200"
      : "border-slate-200";

  const headerBg =
    state === "success"
      ? "bg-emerald-50 border-emerald-200"
      : state === "failure"
      ? "bg-rose-50 border-rose-200"
      : "bg-slate-50 border-slate-200";

  if (typeof item !== "object" || item === null) {
    return (
      <div className={`border ${borderColor} rounded mb-3 overflow-hidden`}>
        <div className={`px-3 py-2 border-b ${headerBg}`}>
          <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Tentativa #{index + 1}
          </span>
        </div>
        <div className="p-3">
          <pre style={preStyle}>{String(item)}</pre>
        </div>
      </div>
    );
  }

  const payloadIsObject = payload && typeof payload === "object";

  return (
    <div className={`border ${borderColor} rounded mb-3 overflow-hidden`}>
      <div
        className={`px-3 py-2 border-b ${headerBg} d-flex align-items-center flex-wrap`}
        style={{ gap: 8 }}
      >
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
          Tentativa #{index + 1}
        </span>
        <StatusPill state={state} />
        <span
          className="text-slate-800"
          style={{
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            fontSize: 14,
            fontWeight: 500,
            flex: 1,
            minWidth: 0,
          }}
        >
          {String(headerMessage).replace(/^"+|"+$/g, "")}
        </span>
      </div>
      <div className="p-3">
        <SummaryTable rows={summary} />

        {payloadIsObject ? (
          <Section title="Payload enviado">
            <pre style={preStyle}>{JSON.stringify(payload, null, 2)}</pre>
          </Section>
        ) : null}

        {stack ? (
          <div className="mb-3">
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-slate-700 bg-transparent border-0 p-0"
              onClick={() => setShowStack((v) => !v)}
            >
              {showStack ? "Ocultar stack trace" : "Mostrar stack trace"}
            </button>
            {showStack && (
              <pre
                style={{
                  ...preStyle,
                  marginTop: 8,
                  fontSize: 11,
                  color: "#64748b",
                }}
              >
                {stack}
              </pre>
            )}
          </div>
        ) : null}

        <div>
          <button
            type="button"
            className="text-xs text-slate-500 hover:text-slate-700 bg-transparent border-0 p-0"
            onClick={() => setShowRaw((v) => !v)}
          >
            {showRaw ? "Ocultar JSON completo" : "Ver JSON completo"}
          </button>
          {showRaw && (
            <pre style={{ ...preStyle, marginTop: 8 }}>
              {JSON.stringify(item, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ModalDetails({ args, show, onHide }) {
  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState([]);

  useEffect(() => {
    setIsLoading(true);
    const parsed = parseRawContent(args?.content?.[0]);
    setContent(parsed);
    setIsLoading(false);
  }, [args?.content]);

  return (
    <Modal size="lg" show={show} onHide={() => onHide()} scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{args.modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {isLoading ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        ) : content.length === 0 ? (
          <div className="text-center text-slate-500 py-4">
            Sem detalhes para exibir.
          </div>
        ) : (
          content.map((item, index) => (
            <ItemCard key={index} item={item} index={index} />
          ))
        )}
      </Modal.Body>
    </Modal>
  );
}
